import { Reveal } from "@components/ui";

const featureCards = [
  {
    title: "24/7 Gas Safety & Emergency Response",
    description: "Emergencies can happen anytime — and we're always ready. SLA response:",
    bullets: [
      "Dubai 60 mins",
      "Sharjah & Ajman 90 mins",
      "Abu Dhabi 120 mins",
    ],
    bulletColor: "#f03813",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" stroke="currentColor" strokeWidth="1.5" />
        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    iconColor: "#f03813",
  },
  {
    title: "Transparent & Easy Billing",
    description: "We keep your bills clear and simple:",
    bullets: [
      "Detailed consumption",
      "No hidden fees",
      "AED 20 billing/admin fee",
      "Online payments",
      "Smart meter accuracy",
    ],
    bulletColor: "#0a7c3e",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <rect x="2" y="3" width="20" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M2 9h20M8 15h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
    iconColor: "#0a7c3e",
  },
  {
    title: "Smart Metering for Accurate Readings",
    description: "With smart meters, you benefit from:",
    bullets: [
      "Accurate bills",
      "Automated readings",
      "No more manual submissions",
      "Detect abnormal usage early",
      "Monthly consumption analytics",
    ],
    bulletColor: "#122d63",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path d="M18 20V10M12 20V4M6 20v-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    iconColor: "#122d63",
  },
];

const smallCards = [
  {
    title: "Safe Installations – DCD & ESMA Certified",
    description: "Your home gas system follows the highest safety standards:",
    bullets: [
      "UAE Fire & Life Safety Code",
      "Dubai Civil Defense approvals",
      "ESMA-certified pipes, hoses & regulators",
      "Certified installation technicians",
    ],
    bulletColor: "#7c3aed",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    iconColor: "#7c3aed",
  },
  {
    title: "Easy Move-In & Move-Out Process",
    description: "No delays, no headaches. We handle:",
    bullets: [
      "New connection setup",
      "Meter activation",
      "Move-out final bill",
      "Refund of security deposit",
    ],
    bulletColor: "#ea580c",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    iconColor: "#ea580c",
  },
];

export function WhyTrustSection() {
  return (
    <section className="w-full bg-[#f1f1f1] overflow-hidden py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-8 md:gap-12 lg:gap-16">
        {/* Header */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-3 md:gap-4 text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/8 text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              Why GASIS
            </span>
            <h2 className="text-[24px] md:text-[40px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
              Why Residential Customers Trust GASIS
            </h2>
            <p className="max-w-[966px] text-[14px] md:text-base lg:text-[28px] tracking-[-0.04em] text-[#62636c] font-albert-sans leading-[1.4]">
              Experience peace of mind with our comprehensive gas solutions
            </p>
          </div>
        </Reveal>

        {/* Feature Cards */}
        <div className="w-full flex flex-col gap-4 md:gap-6">
          {/* Row 1: 3 cards with watermark numbers */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {featureCards.map((card, index) => (
              <Reveal key={card.title} direction="up" delay={index * 100} className="flex">
                <div className="relative flex-1 rounded-[12px] md:rounded-[24px] bg-white overflow-hidden p-4 md:p-6 flex flex-col gap-4 md:gap-6 group hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300 border border-transparent hover:border-[#e8e9ed]">
                  {/* Watermark Number */}
                  <span className="absolute top-3 right-4 md:top-4 md:right-6 text-[48px] md:text-[72px] font-bold text-[#122d63]/[0.04] font-albert-sans leading-none select-none pointer-events-none">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  {/* Icon Container */}
                  <div
                    className="w-10 h-10 md:w-12 md:h-12 rounded-[12px] flex items-center justify-center"
                    style={{ backgroundColor: `${card.iconColor}14`, color: card.iconColor }}
                  >
                    {card.icon}
                  </div>

                  <div className="flex flex-col gap-2 md:gap-[18px]">
                    <h3 className="text-[20px] md:text-[24px] tracking-[-0.04em] font-semibold text-[#20262b] font-albert-sans leading-[1.2]">
                      {card.title}
                    </h3>
                    <p className="text-[14px] md:text-base lg:text-[20px] tracking-[-0.04em] leading-8 text-[#62636c] font-albert-sans">
                      {card.description}
                    </p>
                    <div className="flex flex-col gap-2.5 md:gap-3">
                      {card.bullets.map((bullet) => (
                        <div key={bullet} className="flex items-center gap-2.5">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                            style={{ backgroundColor: `${card.bulletColor}26` }}
                          >
                            <svg viewBox="0 0 12 12" className="w-3 h-3">
                              <path d="M10 3L4.5 8.5 2 6" stroke={card.bulletColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                          </div>
                          <span className="text-[14px] md:text-base lg:text-[18px] tracking-[-0.04em] leading-[26px] text-[rgba(10,14,46,0.75)] font-albert-sans">
                            {bullet}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Row 2: 2 cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {smallCards.map((card, index) => (
              <Reveal key={card.title} direction="up" delay={index * 100} className="flex">
                <div className="flex-1 rounded-[12px] md:rounded-[24px] bg-white overflow-hidden p-4 md:p-6 flex flex-col gap-4 md:gap-6 group hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300 border border-transparent hover:border-[#e8e9ed]">
                  {/* Icon Container */}
                  <div
                    className="w-10 h-10 md:w-12 md:h-12 rounded-[12px] flex items-center justify-center"
                    style={{ backgroundColor: `${card.iconColor}14`, color: card.iconColor }}
                  >
                    {card.icon}
                  </div>

                  <div className="flex flex-col gap-2 md:gap-[18px]">
                    <h3 className="text-[20px] md:text-[24px] tracking-[-0.04em] font-semibold text-[#20262b] font-albert-sans leading-[1.2]">
                      {card.title}
                    </h3>
                    <p className="text-[14px] md:text-base lg:text-[20px] tracking-[-0.04em] leading-8 text-[#62636c] font-albert-sans">
                      {card.description}
                    </p>
                    <div className="flex flex-col gap-2.5 md:gap-3">
                      {card.bullets.map((bullet) => (
                        <div key={bullet} className="flex items-center gap-2.5">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                            style={{ backgroundColor: `${card.bulletColor}26` }}
                          >
                            <svg viewBox="0 0 12 12" className="w-3 h-3">
                              <path d="M10 3L4.5 8.5 2 6" stroke={card.bulletColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                          </div>
                          <span className="text-[14px] md:text-base lg:text-[18px] tracking-[-0.04em] leading-[26px] text-[rgba(10,14,46,0.75)] font-albert-sans">
                            {bullet}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
